import { useCallback, useEffect, useState } from 'react'
import { getProjectDocuments, type ProjectDocumentDto } from '@/lib/api/v2/projects'

/**
 * SOLY-DOC-001 — loads a project's execution (chantier) documents, newest first. Mirrors the app's
 * manual useState + useEffect fetch pattern (no React Query). `refetch` re-pulls after mutations.
 */
export function useProjectDocuments(projectId: string | undefined) {
  const [documents, setDocuments] = useState<ProjectDocumentDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const refetch = useCallback(async () => {
    if (!projectId) return
    setIsLoading(true)
    setError(null)
    try {
      setDocuments(await getProjectDocuments(projectId))
    } catch (e) {
      setError(e)
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    let cancelled = false
    if (!projectId) {
      setDocuments([])
      return
    }
    setIsLoading(true)
    setError(null)
    getProjectDocuments(projectId)
      .then((docs) => {
        if (!cancelled) setDocuments(docs)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  return { documents, isLoading, error, refetch }
}
